import { useInstance } from '@meraki-internal/react-dependency-injection';
import { INote } from '../INote';
import { INoteTarget } from '../INoteTarget';
import { INoteTemplate } from './TemplatesConfig';
import { TemplateProvider } from './TemplateProvider';
import { MixPanelEventEmitter } from '../../../metrics/MixPanelEventEmitter';
import { getNoteTemplateTrackingLocation } from '../NotesTrackingHelper';
import { AlertPresenter } from '../../../AlertPresenter';
import { NoteTemplateMenu } from './NoteTemplateMenu';

interface INoteTemplatePickerProps {
    note: INote;
    noteTarget: INoteTarget;
    onTemplateClicked: (template: INoteTemplate, append?: boolean) => void;
}

export const NoteTemplatePicker: React.FC<INoteTemplatePickerProps>  = ({ note, noteTarget, onTemplateClicked }) => {
    const templates = useInstance(TemplateProvider).getTemplates(noteTarget);
    const mixpanel = useInstance(MixPanelEventEmitter);
    const alertPresenter = useInstance(AlertPresenter);

    const templateSelected = async (template: INoteTemplate) => {
        let action = 'insert';
        if (note.html) {
            action = await alertPresenter.showAlertV2({
                wider: true,
                backdropDismiss: true,
                header: 'Add or Replace?',
                message: 'Would you like to replace your existing content (it will be deleted), or add the template to your text?',
                options: [{
                    id: 'add',
                    label: 'Add'
                },{
                    id: 'replace',
                    label: 'Replace'
                },{
                    id: 'cancel',
                    label: 'Cancel',
                    role: 'cancel'
                }]
            });
        }

        if (action === 'cancel') {
            return;
        }

        if (action === 'insert' || action === 'replace') {
            onTemplateClicked(template);
        } else if (action === 'add') {
            onTemplateClicked(template, true);
        }

        mixpanel.track('Note Template Applied', () => ({
            action: action,
            template: template.label,
            templateId: template.templateId,
            location: getNoteTemplateTrackingLocation(template.target)
        }));
    };

    return (
        <div style={{paddingLeft: 12}}>
            <NoteTemplateMenu
                templates={templates}
                onItemClick={templateSelected}
            />
        </div>
    );
};
